// frontend/src/lib/firebaseAnalytics.ts
import { Analytics, logEvent as firebaseLogEvent } from 'firebase/analytics';
import { initializeFirebaseCore } from './firebase/firebase'; // Path relative to frontend/src/lib/

// Create a promise that resolves to the Analytics service instance (or null)
// Handle server-side contexts gracefully
let analyticsPromise: Promise<Analytics | null>;

if (typeof window === 'undefined') {
    // We're on the server-side
    console.log(
        '[firebaseAnalytics] Detected server-side context. Returning a dummy Analytics promise.'
    );
    // Return a promise that resolves to null - Analytics can't work server-side anyway
    analyticsPromise = Promise.resolve(null);
} else {
    // We're on the client-side
    analyticsPromise = initializeFirebaseCore()
        .then((services) => {
            // services.analytics is already initialized (or null) by initializeFirebaseCore
            if (services.analytics) {
                console.log(
                    '[firebaseAnalytics] Analytics service obtained from initializeFirebaseCore (client).'
                );
            } else {
                console.log(
                    '[firebaseAnalytics] Analytics service is null from initializeFirebaseCore (client - likely no measurementId or browser support issue).'
                );
            }
            return services.analytics;
        })
        .catch((error) => {
            console.error(
                '[firebaseAnalytics] Error obtaining Analytics service from initializeFirebaseCore (client):',
                error
            );
            // Return null instead of throwing to avoid breaking consumers
            return null;
        });
}

export { analyticsPromise as analytics };

/**
 * Initializes Firebase Analytics by awaiting the analyticsPromise.
 * This function is safe to call in any environment - it will always resolve (to null on server).
 * @returns {Promise<Analytics | null>} A promise that resolves with the Analytics instance or null.
 */
export async function initFirebaseAnalytics(): Promise<Analytics | null> {
    if (typeof window === 'undefined') {
        // Short-circuit on server-side
        return null;
    }

    try {
        const an = await analyticsPromise;
        return an;
    } catch (error) {
        console.error(
            '[initFirebaseAnalytics] Error initializing Firebase Analytics (client):',
            error
        );
        return null;
    }
}

// Helper function that components can use for reactive checking
export function isAnalyticsAvailable(): boolean {
    return typeof window !== 'undefined';
}

/**
 * Logs a custom event to Firebase Analytics.
 * @param eventName - The name of the event to log (e.g., 'sign_up', 'scan_cigar').
 * @param eventParams - An object containing parameters for the event.
 */
export async function logFirebaseEvent(
    eventName: string,
    eventParams?: { [key: string]: any }
): Promise<void> {
    if (!isAnalyticsAvailable()) {
        console.warn(
            `[logFirebaseEvent] Analytics not available (server-side or disabled), cannot log event: ${eventName}`
        );
        return;
    }

    try {
        const analyticsInstance = await analyticsPromise;
        if (analyticsInstance) {
            firebaseLogEvent(analyticsInstance, eventName, eventParams);
            console.log(
                `[logFirebaseEvent] Event "${eventName}" logged successfully.`,
                eventParams
            );
        } else {
            console.warn(
                `[logFirebaseEvent] Analytics instance not available, event "${eventName}" not logged.`
            );
        }
    } catch (error) {
        console.error(
            `[logFirebaseEvent] Error logging event "${eventName}":`,
            error
        );
    }
}
